"use client";

import classnames from "classnames";
import Image from "next/image";
import Link from "next/link";
import Skeleton from "react-loading-skeleton";
import { useActivitySummary } from "~/hooks/activity";
import { appendSearchParams } from "~/utils/url";
import { Box } from "../Box/Box";
import { Text } from "../Text/Text";
import styles from "./HeaderAccountStatus.module.css";

const ICON_WIDTH_PIXELS = 44;
const ICON_HEIGHT_PIXELS = 38;

interface Props {
  locationRef: string;
  authenticated: boolean;
  firstName: string | null | undefined;
  isLoading: boolean;
}

export function HeaderAccountStatus({
  locationRef,
  authenticated,
  firstName,
  isLoading,
}: Props): React.ReactNode {
  if (isLoading) return <StatusSkeleton />;

  if (!authenticated) return <SignInPrompt locationRef={locationRef} />;

  return <LoyaltyStatusLink locationRef={locationRef} firstName={firstName} />;
}

function LoyaltyStatusLink({
  locationRef,
  firstName,
}: {
  locationRef: string;
  firstName: string | null | undefined;
}): React.ReactNode {
  const { summary, isLoading } = useActivitySummary();

  return (
    <Link
      className={styles.link}
      href={`/${locationRef}/loyalty`}
      prefetch={false}
    >
      <JetsManIcon collapsible />
      {firstName ? (
        <StatusText transform="uppercase" className={styles.firstName}>
          {firstName}
        </StatusText>
      ) : null}
      {isLoading ? (
        <Skeleton width={56} height={14} />
      ) : (
        <Box
          key={summary.points}
          className={classnames(styles.pointsPulse, styles.points)}
        >
          <StatusText color="primary">
            {summary.points.toLocaleString()} PTS
          </StatusText>
        </Box>
      )}
    </Link>
  );
}

function SignInPrompt({
  locationRef,
}: {
  locationRef: string;
}): React.ReactNode {
  return (
    <Link
      className={styles.link}
      href={appendSearchParams("/login/rewards", {
        next: `/${locationRef}/menu`,
        location: locationRef,
      })}
      prefetch={false}
    >
      <JetsManIcon />
      <StatusText transform="uppercase">Sign In</StatusText>
    </Link>
  );
}

function JetsManIcon({
  collapsible = false,
}: {
  collapsible?: boolean;
} = {}): React.ReactNode {
  return (
    <Image
      alt=""
      src="/images/jetman_circle.png"
      width={ICON_WIDTH_PIXELS}
      height={ICON_HEIGHT_PIXELS}
      className={classnames(styles.icon, collapsible && styles.collapsibleIcon)}
    />
  );
}

function StatusSkeleton(): React.ReactNode {
  return (
    <Box display="flex" alignItems="center">
      <Skeleton width={90} height={14} />
    </Box>
  );
}

function StatusText({
  children,
  color = "white",
  transform,
  className,
}: {
  children: React.ReactNode;
  color?: "white" | "primary";
  transform?: "uppercase";
  className?: string;
}): React.ReactNode {
  return (
    <Text
      size="small"
      weight="strong"
      color={color}
      transform={transform}
      whiteSpace="nowrap"
      className={classnames(styles.condensed, className)}
      truncate
    >
      {children}
    </Text>
  );
}
